import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Papa from 'papaparse';
import api, { downloadBlob } from '../services/api';
import MonthPicker from '../components/MonthPicker';
import { useCutoffDay, monthStart, showDate, todayIso } from '../utils/months';

const styles = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 },
  h1: { fontSize: 22, fontWeight: 600 },
  btn: { padding: '8px 16px', backgroundColor: '#4a9eff', color: '#fff', border: 'none', borderRadius: 4, fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  searchRow: { display: 'flex', gap: 12, marginBottom: 16 },
  search: { flex: 1, padding: '10px 14px', border: '1px solid #ddd', borderRadius: 4, fontSize: 14 },
  table: { width: '100%', borderCollapse: 'collapse', backgroundColor: '#fff', borderRadius: 8, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' },
  th: { textAlign: 'left', padding: '10px 14px', backgroundColor: '#f8f9fa', fontSize: 12, fontWeight: 600, color: '#666', textTransform: 'uppercase', borderBottom: '1px solid #eee', cursor: 'pointer' },
  td: { padding: '10px 14px', borderBottom: '1px solid #f0f0f0', fontSize: 14 },
  link: { color: '#4a9eff', textDecoration: 'none', fontWeight: 500 },
  pagination: { display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 },
  pageBtn: (active) => ({
    padding: '6px 12px', border: '1px solid #ddd', borderRadius: 4, fontSize: 13,
    backgroundColor: active ? '#4a9eff' : '#fff', color: active ? '#fff' : '#333', cursor: 'pointer',
  }),
  modal: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex',
    alignItems: 'center', justifyContent: 'center', zIndex: 1000,
  },
  modalCard: {
    backgroundColor: '#fff', borderRadius: 8, padding: 24, width: 520,
    maxHeight: '90vh', overflow: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
  },
  modalTitle: { fontSize: 18, fontWeight: 600, marginBottom: 16 },
  label: { display: 'block', fontSize: 13, fontWeight: 500, color: '#555', marginBottom: 4 },
  input: { width: '100%', padding: '8px 10px', border: '1px solid #ddd', borderRadius: 4, fontSize: 14, marginBottom: 12 },
  select: { width: '100%', padding: '8px 10px', border: '1px solid #ddd', borderRadius: 4, fontSize: 14, marginBottom: 12 },
  row: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 },
  btnRow: { display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 },
  btnCancel: { padding: '8px 16px', backgroundColor: '#f0f0f0', border: 'none', borderRadius: 4, fontSize: 13, cursor: 'pointer' },
  error: { backgroundColor: '#fee', color: '#c0392b', padding: '8px 12px', borderRadius: 4, fontSize: 13, marginBottom: 12 },
  checkbox: { width: 16, height: 16, cursor: 'pointer' },
  btnPrint: { padding: '8px 16px', backgroundColor: '#27ae60', color: '#fff', border: 'none', borderRadius: 4, fontSize: 13, fontWeight: 600, cursor: 'pointer' },
};

// --- CSV Import column mapping aliases ---
const FIELD_ALIASES = {
  first_name: ['first name', 'firstname', 'fname', 'forename', 'given name', 'givenname'],
  last_name: ['last name', 'lastname', 'lname', 'surname', 'family name', 'familyname'],
  company: ['company', 'organisation', 'organization', 'org', 'business', 'company name'],
  subscriber_number: ['subscriber number', 'sub number', 'sub no', 'sub #', 'subscriber no', 'subscriber #', 'sub id', 'subscriber id', 'account', 'account number'],
  address_line1: ['address', 'address line 1', 'address1', 'street', 'street address', 'addr1', 'address_line_1'],
  address_line2: ['address line 2', 'address2', 'addr2', 'address_line_2', 'apt', 'apartment', 'unit'],
  city: ['city', 'town', 'locality'],
  county: ['county', 'state', 'province', 'region'],
  postcode: ['postcode', 'post code', 'zip', 'zip code', 'zipcode', 'postal code', 'postalcode', 'eircode'],
  country: ['country', 'nation'],
  email: ['email', 'e-mail', 'email address', 'emailaddress'],
  phone: ['phone', 'telephone', 'tel', 'mobile', 'phone number', 'phonenumber'],
  notes: ['notes', 'note', 'comments', 'comment'],
  start_date: ['start', 'start date', 'subscription start', 'started', 'date started', 'from', 'from date', 'joined', 'date joined'],
  end_date: ['expiry', 'expiry date', 'expires', 'expire', 'expiration', 'expiration date', 'end', 'end date', 'subscription end',
    'renewal', 'renewal date', 'renewal due', 'due date', 'paid until', 'paid to', 'paid up to', 'valid until', 'until', 'to date'],
  amount_paid: ['amount', 'amount paid', 'paid', 'price', 'fee', 'payment', 'subscription fee', 'cost'],
};

const SUBSCRIBER_FIELDS = [
  { value: '', label: '— Skip —' },
  { value: 'subscriber_number', label: 'Subscriber Number' },
  { value: 'first_name', label: 'First Name *' },
  { value: 'last_name', label: 'Last Name *' },
  { value: 'company', label: 'Company' },
  { value: 'address_line1', label: 'Address Line 1 *' },
  { value: 'address_line2', label: 'Address Line 2' },
  { value: 'city', label: 'City *' },
  { value: 'county', label: 'County' },
  { value: 'postcode', label: 'Postcode / Eircode' },
  { value: 'country', label: 'Country' },
  { value: 'email', label: 'Email (optional)' },
  { value: 'phone', label: 'Phone' },
  { value: 'notes', label: 'Notes' },
  { value: 'start_date', label: 'Subscription Start' },
  { value: 'end_date', label: 'Subscription Expiry' },
  { value: 'amount_paid', label: 'Amount Paid' },
];

const REQUIRED_FIELDS = ['first_name', 'last_name', 'address_line1', 'city'];

function autoMapColumn(header) {
  // "First Name*", "Expiry *", "E-mail:" → first name, expiry, e mail
  const h = header.toLowerCase().replace(/[*:]+/g, ' ').replace(/[_\-]/g, ' ').replace(/\s+/g, ' ').trim();
  for (const [field, aliases] of Object.entries(FIELD_ALIASES)) {
    if (aliases.includes(h) || h === field.replace(/_/g, ' ')) return field;
  }
  return '';
}

function ImportModal({ onClose, onComplete }) {
  const [step, setStep] = useState(1);
  const [csvData, setCsvData] = useState(null);
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [mapping, setMapping] = useState({});
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [parseError, setParseError] = useState('');
  // Optional: subscribe everyone in the file to a title
  const [titles, setTitles] = useState([]);
  const [titleId, setTitleId] = useState('');
  const [startDefault, setStartDefault] = useState(() => new Date().toLocaleDateString('en-CA'));
  // sold by the month (Settings): the dates given here are months
  const cutoff = useCutoffDay();
  const [endDefault, setEndDefault] = useState('');
  // The server's dry run of the import: what it will do, row errors, first rows as read
  const [plan, setPlan] = useState(null);
  const [planError, setPlanError] = useState('');
  const [planning, setPlanning] = useState(false);

  useEffect(() => {
    api.getTitles().then(data => setTitles((data || []).filter(t => t.active !== false && t.active !== 0))).catch(() => {});
  }, []);

  const handleFile = (file) => {
    setParseError('');
    if (!file) return;
    if (!file.name.match(/\.(csv|tsv|txt)$/i)) {
      setParseError('Please select a CSV file');
      return;
    }
    // Read as plain rows: a spreadsheet saved as CSV often has empty lines (",,,,") above the
    // headings, so the first line with something in it is the header. Each row keeps its line
    // number in the file, which the server's messages use.
    Papa.parse(file, {
      header: false,
      skipEmptyLines: false,
      complete: (results) => {
        const lines = results.data.map((cells, i) => ({ cells: cells.map(c => String(c ?? '')), line: i + 1 }))
          .filter(r => r.cells.some(c => c.trim()));
        if (lines.length < 2) {
          setParseError('CSV file is empty or has no data rows');
          return;
        }
        const seen = {};
        const fields = lines[0].cells.map((c, i) => {
          let name = c.trim() || `Column ${i + 1}`;
          if (seen[name]) name = `${name} (${++seen[name]})`; else seen[name] = 1;
          return name;
        });
        const data = lines.slice(1).map(r => {
          const row = { __line: r.line };
          fields.forEach((f, i) => { row[f] = r.cells[i] ?? ''; });
          return row;
        });
        if (data.length > 5000) {
          setParseError(`Too many rows (${data.length}). Maximum 5,000 per import.`);
          return;
        }
        setCsvData(data);
        setCsvHeaders(fields);
        // Auto-map columns
        const autoMap = {};
        for (const header of fields) {
          autoMap[header] = autoMapColumn(header);
        }
        setMapping(autoMap);
        setStep(2);
      },
      error: (err) => {
        setParseError(`Parse error: ${err.message}`);
      }
    });
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const mappedFields = Object.values(mapping).filter(v => v);
  const requiredMapped = REQUIRED_FIELDS.every(f => mappedFields.includes(f));

  // Build mapped rows for preview
  const buildMappedRows = () => {
    if (!csvData) return [];
    return csvData.map(row => {
      const mapped = { _line: row.__line };
      for (const [csvCol, field] of Object.entries(mapping)) {
        if (field) mapped[field] = row[csvCol] || '';
      }
      return mapped;
    });
  };

  const endMapped = mappedFields.includes('end_date');
  const subscriptionReady = !titleId || endMapped || !!endDefault;
  const importOptions = () => (titleId
    ? { subscription: { title_id: titleId, start_date: startDefault || null, end_date: endDefault || null } }
    : {});

  // All rows go to the server (its errors name the line of the file); it reads the dates
  const goToPreview = async () => {
    setStep(3);
    setPlan(null);
    setPlanError('');
    setPlanning(true);
    try {
      setPlan(await api.importSubscribers(buildMappedRows(), { ...importOptions(), dry_run: true }));
    } catch (err) {
      setPlanError(err.message || 'Could not check the file');
    }
    setPlanning(false);
  };

  const planCount = plan ? plan.summary.new_subscribers + plan.summary.existing_subscribers : 0;

  const handleImport = async () => {
    setImporting(true);
    try {
      const res = await api.importSubscribers(buildMappedRows(), importOptions());
      setResult(res);
      setStep(4);
    } catch (err) {
      setResult({ created: 0, skipped: 0, errors: [{ row: 0, error: err.message || 'Import failed' }] });
      setStep(4);
    }
    setImporting(false);
  };

  const modalStyle = {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex',
    alignItems: 'center', justifyContent: 'center', zIndex: 1000,
  };
  const cardStyle = {
    backgroundColor: '#fff', borderRadius: 8, padding: 24, width: 700,
    maxHeight: '90vh', overflow: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
  };

  return (
    <div style={modalStyle} onClick={onClose}>
      <div style={cardStyle} onClick={e => e.stopPropagation()}>
        {/* Step indicator */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
          {['Upload', 'Map Columns', 'Preview', 'Results'].map((label, i) => (
            <div key={i} style={{
              flex: 1, textAlign: 'center', padding: '6px 0', fontSize: 12, fontWeight: 600,
              borderBottom: `3px solid ${step === i + 1 ? '#4a9eff' : step > i + 1 ? '#27ae60' : '#ddd'}`,
              color: step === i + 1 ? '#4a9eff' : step > i + 1 ? '#27ae60' : '#999',
            }}>{i + 1}. {label}</div>
          ))}
        </div>

        {/* Step 1: Upload */}
        {step === 1 && (
          <div>
            <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 12 }}>Upload CSV File</div>
            <div
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              style={{
                border: `2px dashed ${dragOver ? '#4a9eff' : '#ddd'}`,
                borderRadius: 8, padding: 40, textAlign: 'center',
                backgroundColor: dragOver ? '#f0f7ff' : '#fafafa',
                cursor: 'pointer', transition: 'all 0.2s',
              }}
              onClick={() => document.getElementById('csv-file-input').click()}
            >
              <div style={{ fontSize: 32, marginBottom: 8 }}>&#128196;</div>
              <div style={{ fontSize: 14, color: '#666', marginBottom: 4 }}>Drag & drop a CSV file here, or click to browse</div>
              <div style={{ fontSize: 12, color: '#999' }}>Supports .csv, .tsv, .txt — max 5,000 rows</div>
              <input
                id="csv-file-input"
                type="file"
                accept=".csv,.tsv,.txt"
                style={{ display: 'none' }}
                onChange={e => handleFile(e.target.files[0])}
              />
            </div>
            {parseError && <div style={{ color: '#e74c3c', fontSize: 13, marginTop: 8 }}>{parseError}</div>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
              <button onClick={onClose} style={styles.btnCancel}>Cancel</button>
            </div>
          </div>
        )}

        {/* Step 2: Column Mapping */}
        {step === 2 && (
          <div>
            <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 4 }}>Map Columns</div>
            <div style={{ fontSize: 13, color: '#666', marginBottom: 12 }}>
              Match each CSV column to a subscriber field. Fields marked * are required.
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr>
                  <th style={{ ...styles.th, fontSize: 11 }}>CSV Column</th>
                  <th style={{ ...styles.th, fontSize: 11 }}>Sample Value</th>
                  <th style={{ ...styles.th, fontSize: 11 }}>Maps To</th>
                </tr>
              </thead>
              <tbody>
                {csvHeaders.map(header => (
                  <tr key={header}>
                    <td style={{ ...styles.td, fontWeight: 500 }}>{header}</td>
                    <td style={{ ...styles.td, color: '#888', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {csvData[0]?.[header] || '—'}
                    </td>
                    <td style={styles.td}>
                      <select
                        value={mapping[header] || ''}
                        onChange={e => setMapping(m => ({ ...m, [header]: e.target.value }))}
                        style={{ ...styles.select, marginBottom: 0, fontSize: 13 }}
                      >
                        {SUBSCRIBER_FIELDS.map(f => (
                          <option key={f.value} value={f.value}>{f.label}</option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!requiredMapped && (
              <div style={{ color: '#e67e22', fontSize: 12, marginTop: 8 }}>
                All required fields must be mapped: First Name, Last Name, Address Line 1, City
              </div>
            )}
            <div style={{ fontSize: 12, color: '#888', marginTop: 8 }}>
              Email and Eircode are optional: subscribers without an email address get their renewal reminders by post.
            </div>

            <div style={{ marginTop: 16, padding: 12, backgroundColor: '#f8f9fa', borderRadius: 6 }}>
              <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>Subscribe them to a title (optional)</div>
              <select
                value={titleId}
                onChange={e => setTitleId(e.target.value)}
                style={{ ...styles.select, fontSize: 13 }}
                data-testid="import-title"
              >
                <option value="">— Don't subscribe them to a title —</option>
                {titles.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
              {titleId && (
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  <label style={{ flex: '1 1 200px', fontSize: 12, color: '#555' }}>
                    {endMapped ? `Expiry ${cutoff ? 'month' : 'date'} where the file has none` : `Expiry ${cutoff ? 'month' : 'date'} for everyone *`}
                    {cutoff ? (
                      <div style={{ marginTop: 4 }}>
                        <MonthPicker value={endDefault} onChange={setEndDefault} style={{ ...styles.input, fontSize: 13 }} testId="import-end-default" />
                      </div>
                    ) : (
                      <input type="date" value={endDefault} onChange={e => setEndDefault(e.target.value)}
                        style={{ ...styles.input, fontSize: 13, marginTop: 4 }} data-testid="import-end-default" />
                    )}
                  </label>
                  <label style={{ flex: '1 1 200px', fontSize: 12, color: '#555' }}>
                    {cutoff ? 'First issue where the file has none' : 'Start date where the file has none'}
                    {cutoff ? (
                      <div style={{ marginTop: 4 }}>
                        <MonthPicker value={monthStart(startDefault)} onChange={setStartDefault} style={{ ...styles.input, fontSize: 13 }} />
                      </div>
                    ) : (
                      <input type="date" value={startDefault} onChange={e => setStartDefault(e.target.value)}
                        style={{ ...styles.input, fontSize: 13, marginTop: 4 }} />
                    )}
                  </label>
                  <div style={{ fontSize: 12, color: '#888', flexBasis: '100%' }}>
                    {endMapped
                      ? (cutoff
                        ? 'Each row\'s expiry comes from the file, as a month: October 2027, Oct 2027, 10/2027 or a full date (31/10/2027 → October 2027).'
                        : 'Each row\'s expiry comes from the file. Dates in any common form are read (31/10/2026, 31 Oct 2026, 2026-10-31…).')
                      : 'Tip: map a column to Subscription Expiry to give each subscriber their own date.'}
                  </div>
                </div>
              )}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16 }}>
              <button onClick={() => setStep(1)} style={styles.btnCancel}>Back</button>
              <button
                onClick={goToPreview}
                disabled={!requiredMapped || !subscriptionReady}
                style={{ ...styles.btn, opacity: requiredMapped && subscriptionReady ? 1 : 0.5 }}
                data-testid="import-next"
              >Next</button>
            </div>
          </div>
        )}

        {/* Step 3: Preview — the server's dry run */}
        {step === 3 && (
          <div>
            <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>Preview Import</div>
            {planning && <div style={{ fontSize: 13, color: '#888' }}>Checking {csvData.length} rows…</div>}
            {planError && <div style={{ color: '#e74c3c', fontSize: 13 }}>{planError}</div>}
            {plan && (
              <div data-testid="import-plan">
                <ul style={{ fontSize: 13, margin: '0 0 12px', paddingLeft: 18, lineHeight: 1.7 }}>
                  <li><b>{plan.summary.new_subscribers}</b> new subscriber{plan.summary.new_subscribers === 1 ? '' : 's'}
                    {plan.summary.no_email > 0 && <> ({plan.summary.no_email} without an email address: their reminders go by post)</>}</li>
                  {plan.summary.existing_subscribers > 0 && (
                    <li><b>{plan.summary.existing_subscribers}</b> already on file (same number and surname): they just get the subscription</li>
                  )}
                  {plan.summary.title && (
                    <li><b>{plan.summary.subscriptions}</b> subscription{plan.summary.subscriptions === 1 ? '' : 's'} to <b>{plan.summary.title}</b>
                      {plan.summary.expired > 0 && <>, {plan.summary.expired} of them already expired</>}</li>
                  )}
                  {(plan.summary.start_order === 'month-first' || plan.summary.end_order === 'month-first') && (
                    <li>Dates in this file are written month first (e.g. 10/31/2026) and were read that way</li>
                  )}
                  {plan.errors.length > 0 && (
                    <li style={{ color: '#c0392b' }}><b>{plan.errors.length}</b> row{plan.errors.length === 1 ? '' : 's'} will be skipped (listed below)</li>
                  )}
                </ul>
                {plan.summary.reminders_due > 0 && (
                  <div style={{ fontSize: 13, padding: '10px 12px', backgroundColor: '#fff8e6', border: '1px solid #f5d38a', borderRadius: 6, marginBottom: 12 }}
                    data-testid="import-reminders-warning">
                    <b>{plan.summary.reminders_due}</b> of these subscriptions expire within {plan.summary.reminder_window_days} days, so a renewal reminder is due now.
                    {plan.summary.auto_send && plan.summary.reminders_auto_email > 0
                      ? <> Automatic reminders are on: <b>{plan.summary.reminders_auto_email}</b> will be emailed within about 15 minutes of importing.</>
                      : null}
                    {plan.summary.reminders_due - plan.summary.reminders_auto_email > 0 && (
                      <> {plan.summary.reminders_due - plan.summary.reminders_auto_email} will be listed on the Renewals page.</>
                    )}
                  </div>
                )}
                {plan.preview.length > 0 && (
                  <div style={{ overflowX: 'auto', maxHeight: 300 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                      <thead>
                        <tr>
                          {['Line', 'Sub #', 'Name', 'Email', ...(plan.summary.title ? ['Start', 'Expiry'] : [])].map(h => (
                            <th key={h} style={{ ...styles.th, fontSize: 10 }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {plan.preview.map(r => (
                          <tr key={r.line}>
                            <td style={{ ...styles.td, fontSize: 11, color: '#888' }}>{r.line}</td>
                            <td style={{ ...styles.td, fontSize: 11 }}>{r.subscriber_number || '—'}</td>
                            <td style={{ ...styles.td, fontSize: 11 }}>{r.name}{r.existing && <span style={{ color: '#888' }}> (on file)</span>}</td>
                            <td style={{ ...styles.td, fontSize: 11, color: r.email ? undefined : '#888' }}>{r.email || 'by post'}</td>
                            {plan.summary.title && <td style={{ ...styles.td, fontSize: 11 }}>{r.start}</td>}
                            {plan.summary.title && (
                              <td style={{ ...styles.td, fontSize: 11, color: r.status === 'expired' ? '#c0392b' : undefined }}>
                                {r.end}{r.status === 'expired' ? ' (expired)' : ''}
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {planCount > plan.preview.length && (
                  <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>Showing the first {plan.preview.length} of {planCount}</div>
                )}
                {plan.errors.length > 0 && (
                  <div style={{ maxHeight: 160, overflow: 'auto', marginTop: 12 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                      <thead>
                        <tr>
                          <th style={{ ...styles.th, fontSize: 10 }}>Line</th>
                          <th style={{ ...styles.th, fontSize: 10 }}>Skipped because</th>
                        </tr>
                      </thead>
                      <tbody>
                        {plan.errors.slice(0, 100).map((e, i) => (
                          <tr key={i}>
                            <td style={{ ...styles.td, fontSize: 11, width: 50 }}>{e.row}</td>
                            <td style={{ ...styles.td, fontSize: 11, color: '#c0392b' }}>{e.error}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16 }}>
              <button onClick={() => setStep(2)} style={styles.btnCancel}>Back</button>
              <button
                onClick={handleImport}
                disabled={importing || planning || !plan || planCount === 0}
                style={{ ...styles.btn, backgroundColor: '#27ae60', opacity: (importing || planning || !plan || planCount === 0) ? 0.5 : 1 }}
                data-testid="import-go"
              >{importing ? 'Importing...'
                : plan && plan.summary.title
                  ? `Import ${plan.summary.new_subscribers} subscribers + ${plan.summary.subscriptions} subscriptions`
                  : `Import ${plan ? plan.summary.new_subscribers : ''} Subscribers`}</button>
            </div>
          </div>
        )}

        {/* Step 4: Results */}
        {step === 4 && result && (
          <div>
            <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 12 }}>Import Complete</div>
            <div style={{ display: 'flex', gap: 20, marginBottom: 16 }}>
              <div style={{ padding: '12px 20px', backgroundColor: '#e6f9e6', borderRadius: 8, textAlign: 'center' }}>
                <div style={{ fontSize: 24, fontWeight: 700, color: '#27ae60' }}>{result.created}</div>
                <div style={{ fontSize: 12, color: '#666' }}>Created</div>
              </div>
              {result.subscriptions_created > 0 && (
                <div style={{ padding: '12px 20px', backgroundColor: '#e6f0ff', borderRadius: 8, textAlign: 'center' }}>
                  <div style={{ fontSize: 24, fontWeight: 700, color: '#4a9eff' }}>{result.subscriptions_created}</div>
                  <div style={{ fontSize: 12, color: '#666' }}>Subscriptions</div>
                </div>
              )}
              <div style={{ padding: '12px 20px', backgroundColor: '#fff8e6', borderRadius: 8, textAlign: 'center' }}>
                <div style={{ fontSize: 24, fontWeight: 700, color: '#e67e22' }}>{result.skipped}</div>
                <div style={{ fontSize: 12, color: '#666' }}>Skipped</div>
              </div>
              {result.errors?.length > 0 && (
                <div style={{ padding: '12px 20px', backgroundColor: '#fee', borderRadius: 8, textAlign: 'center' }}>
                  <div style={{ fontSize: 24, fontWeight: 700, color: '#e74c3c' }}>{result.errors.length}</div>
                  <div style={{ fontSize: 12, color: '#666' }}>Errors</div>
                </div>
              )}
            </div>
            {result.errors?.length > 0 && (
              <div style={{ maxHeight: 200, overflow: 'auto', marginBottom: 12 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr>
                      <th style={{ ...styles.th, fontSize: 10 }}>Line</th>
                      <th style={{ ...styles.th, fontSize: 10 }}>Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.errors.slice(0, 50).map((e, i) => (
                      <tr key={i}>
                        <td style={{ ...styles.td, fontSize: 11, width: 50 }}>{e.row}</td>
                        <td style={{ ...styles.td, fontSize: 11, color: '#c0392b' }}>{e.error}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {result.errors.length > 50 && (
                  <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>Showing first 50 of {result.errors.length} errors</div>
                )}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
              <button onClick={() => { onComplete(); onClose(); }} style={styles.btn}>Done</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


const emptyForm = {
  subscriber_number: '', first_name: '', last_name: '', company: '', address_line1: '', address_line2: '',
  city: '', county: '', postcode: '', country: 'Ireland', email: '', phone: '',
  notes: '', preferred_renewal_method: 'email',
  delivery_name: '', delivery_address_line1: '', delivery_address_line2: '', delivery_city: '',
  delivery_county: '', delivery_postcode: '', delivery_country: '',
};

export default function Subscribers() {
  // Expiry as a month when sold by the month (latest end date, red once passed)
  const cutoff = useCutoffDay();
  const [subscribers, setSubscribers] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [titles, setTitles] = useState([]);
  const [filterTitleId, setFilterTitleId] = useState('');
  const navigate = useNavigate();

  // Fetch titles for filter dropdown
  useEffect(() => {
    api.getTitles().then(data => setTitles(data)).catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    const params = { search, page, limit: 25 };
    if (filterTitleId) params.title_id = filterTitleId;
    api.getSubscribers(params).then(data => {
      setSubscribers(data.subscribers);
      setPagination(data.pagination);
    }).finally(() => setLoading(false));
  }, [search, page, filterTitleId]);

  useEffect(() => { load(); }, [load]);

  // Debounced search
  const [searchInput, setSearchInput] = useState('');
  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const handleSave = async () => {
    setFormError('');
    if (!form.first_name || !form.last_name || !form.address_line1 || !form.city) {
      setFormError('First name, last name, address and city are required');
      return;
    }
    try {
      const created = await api.createSubscriber(form);
      setShowModal(false);
      setForm(emptyForm);
      navigate(`/subscribers/${created.id}`);
    } catch (err) {
      setFormError(err.message);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const allOnPage = subscribers.map(s => s.id);
    const allSelected = allOnPage.every(id => selectedIds.has(id));
    if (allSelected) {
      setSelectedIds(prev => {
        const next = new Set(prev);
        allOnPage.forEach(id => next.delete(id));
        return next;
      });
    } else {
      setSelectedIds(prev => {
        const next = new Set(prev);
        allOnPage.forEach(id => next.add(id));
        return next;
      });
    }
  };

  const handlePrintLabels = async () => {
    try {
      const result = await api.generateSubscriberLabels([...selectedIds]);
      downloadBlob(result.blob, 'subscriber-labels.pdf');
      setSelectedIds(new Set());
    } catch (err) {
      alert(err.message || 'Failed to generate labels');
    }
  };

  const setField = (field, value) => setForm(f => ({ ...f, [field]: value }));

  return (
    <div>
      <div style={styles.header}>
        <h1 style={styles.h1}>Subscribers ({pagination.total})</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          {selectedIds.size > 0 && (
            <button style={styles.btnPrint} onClick={handlePrintLabels}>
              Print Labels ({selectedIds.size})
            </button>
          )}
          <button style={{ ...styles.btn, backgroundColor: '#8e44ad' }} onClick={() => setShowImport(true)}>Import CSV</button>
          <button style={styles.btn} onClick={() => setShowModal(true)}>+ Add Subscriber</button>
        </div>
      </div>

      <div style={styles.searchRow}>
        <select
          style={{ ...styles.select, width: 200, marginBottom: 0, flexShrink: 0 }}
          value={filterTitleId}
          onChange={e => { setFilterTitleId(e.target.value); setPage(1); }}
        >
          <option value="">All Titles</option>
          {titles.filter(t => t.active !== false).map(t => (
            <option key={t.id} value={t.id}>{t.name}</option>
          ))}
        </select>
        <input
          style={styles.search}
          placeholder="Search by name, company, email or postcode..."
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
          autoFocus
        />
      </div>

      {loading ? <div>Loading...</div> : (
        <>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={{ ...styles.th, width: 36 }}>
                  <input
                    type="checkbox"
                    style={styles.checkbox}
                    checked={subscribers.length > 0 && subscribers.every(s => selectedIds.has(s.id))}
                    onChange={toggleSelectAll}
                  />
                </th>
                <th style={styles.th}>Full Name</th>
                <th style={styles.th}>Country</th>
                <th style={styles.th}>Expiry</th>
              </tr>
            </thead>
            <tbody>
              {subscribers.map(s => (
                <tr key={s.id}>
                  <td style={styles.td}>
                    <input
                      type="checkbox"
                      style={styles.checkbox}
                      checked={selectedIds.has(s.id)}
                      onChange={() => toggleSelect(s.id)}
                    />
                  </td>
                  <td style={styles.td}>
                    <Link to={`/subscribers/${s.id}`} style={styles.link}>
                      {s.first_name} {s.last_name}
                    </Link>
                  </td>
                  <td style={styles.td}>{s.country || '-'}</td>
                  <td style={{ ...styles.td, color: s.expiry && s.expiry < todayIso() ? '#c0392b' : undefined }}>
                    {showDate(s.expiry, cutoff)}
                  </td>
                </tr>
              ))}
              {subscribers.length === 0 && (
                <tr><td style={styles.td} colSpan={4}>No subscribers found</td></tr>
              )}
            </tbody>
          </table>

          {pagination.pages > 1 && (
            <div style={styles.pagination}>
              {Array.from({ length: pagination.pages }, (_, i) => i + 1).map(p => (
                <button key={p} style={styles.pageBtn(p === page)} onClick={() => setPage(p)}>{p}</button>
              ))}
            </div>
          )}
        </>
      )}

      {showImport && (
        <ImportModal
          onClose={() => setShowImport(false)}
          onComplete={() => load()}
        />
      )}

      {showModal && (
        <div style={styles.modal} onClick={() => setShowModal(false)}>
          <div style={styles.modalCard} onClick={e => e.stopPropagation()}>
            <div style={styles.modalTitle}>Add Subscriber</div>
            {formError && <div style={styles.error}>{formError}</div>}
            <label style={styles.label}>Subscriber Number (optional — leave blank to use auto ID)</label>
            <input style={{ ...styles.input, maxWidth: 200 }} value={form.subscriber_number} onChange={e => setField('subscriber_number', e.target.value)} placeholder="e.g. SUB-001" autoFocus />
            <div style={styles.row}>
              <div>
                <label style={styles.label}>First Name *</label>
                <input style={styles.input} value={form.first_name} onChange={e => setField('first_name', e.target.value)} />
              </div>
              <div>
                <label style={styles.label}>Last Name *</label>
                <input style={styles.input} value={form.last_name} onChange={e => setField('last_name', e.target.value)} />
              </div>
            </div>
            <label style={styles.label}>Company</label>
            <input style={styles.input} value={form.company} onChange={e => setField('company', e.target.value)} />
            <label style={styles.label}>Address Line 1 *</label>
            <input style={styles.input} value={form.address_line1} onChange={e => setField('address_line1', e.target.value)} />
            <label style={styles.label}>Address Line 2</label>
            <input style={styles.input} value={form.address_line2} onChange={e => setField('address_line2', e.target.value)} />
            <div style={styles.row}>
              <div>
                <label style={styles.label}>City *</label>
                <input style={styles.input} value={form.city} onChange={e => setField('city', e.target.value)} />
              </div>
              <div>
                <label style={styles.label}>County</label>
                <input style={styles.input} value={form.county} onChange={e => setField('county', e.target.value)} />
              </div>
            </div>
            <div style={styles.row}>
              <div>
                <label style={styles.label}>Postcode / Eircode</label>
                <input style={styles.input} value={form.postcode} onChange={e => setField('postcode', e.target.value)} />
              </div>
              <div>
                <label style={styles.label}>Country</label>
                <input style={styles.input} value={form.country} onChange={e => setField('country', e.target.value)} />
              </div>
            </div>
            <div style={styles.row}>
              <div>
                <label style={styles.label}>Email</label>
                <input style={styles.input} type="email" value={form.email} onChange={e => setField('email', e.target.value)} />
              </div>
              <div>
                <label style={styles.label}>Phone</label>
                <input style={styles.input} value={form.phone} onChange={e => setField('phone', e.target.value)} />
              </div>
            </div>
            <label style={styles.label}>Preferred Renewal Method</label>
            <select style={styles.select} value={form.preferred_renewal_method} onChange={e => setField('preferred_renewal_method', e.target.value)}>
              <option value="email">Email</option>
              <option value="letter">Letter</option>
              <option value="both">Both</option>
            </select>
            <div style={{ marginTop: 4, marginBottom: 12 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, cursor: 'pointer' }}>
                <input type="checkbox" checked={!!(form.delivery_address_line1 || form._showDelivery)} onChange={e => {
                  if (!e.target.checked) {
                    setForm(f => ({ ...f, delivery_name: '', delivery_address_line1: '', delivery_address_line2: '', delivery_city: '', delivery_county: '', delivery_postcode: '', delivery_country: '', _showDelivery: false }));
                  } else {
                    setForm(f => ({ ...f, _showDelivery: true }));
                  }
                }} />
                Different delivery name / address
              </label>
            </div>
            {(form.delivery_address_line1 || form._showDelivery) && (
              <div style={{ padding: '12px 14px', backgroundColor: '#f8f9fa', borderRadius: 6, border: '1px solid #eee', marginBottom: 12 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#555', marginBottom: 8 }}>Delivery Name &amp; Address</div>
                <label style={styles.label}>Recipient Name</label>
                <input style={styles.input} placeholder="Leave blank to use subscriber name" value={form.delivery_name} onChange={e => setField('delivery_name', e.target.value)} />
                <label style={styles.label}>Address Line 1</label>
                <input style={styles.input} value={form.delivery_address_line1} onChange={e => setField('delivery_address_line1', e.target.value)} />
                <label style={styles.label}>Address Line 2</label>
                <input style={styles.input} value={form.delivery_address_line2} onChange={e => setField('delivery_address_line2', e.target.value)} />
                <div style={styles.row}>
                  <div>
                    <label style={styles.label}>City</label>
                    <input style={styles.input} value={form.delivery_city} onChange={e => setField('delivery_city', e.target.value)} />
                  </div>
                  <div>
                    <label style={styles.label}>County</label>
                    <input style={styles.input} value={form.delivery_county} onChange={e => setField('delivery_county', e.target.value)} />
                  </div>
                </div>
                <div style={styles.row}>
                  <div>
                    <label style={styles.label}>Postcode</label>
                    <input style={styles.input} value={form.delivery_postcode} onChange={e => setField('delivery_postcode', e.target.value)} />
                  </div>
                  <div>
                    <label style={styles.label}>Country</label>
                    <input style={styles.input} value={form.delivery_country} onChange={e => setField('delivery_country', e.target.value)} />
                  </div>
                </div>
              </div>
            )}
            <label style={styles.label}>Notes</label>
            <textarea style={{ ...styles.input, height: 60, resize: 'vertical' }} value={form.notes} onChange={e => setField('notes', e.target.value)} />
            <div style={styles.btnRow}>
              <button style={styles.btnCancel} onClick={() => setShowModal(false)}>Cancel</button>
              <button style={styles.btn} onClick={handleSave}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
